import React from 'react';
import { UseFormRegister, UseFormWatch, FieldArrayWithId } from 'react-hook-form';
import { InvoiceHire } from '../../types';
import Input from './Input';
import TextArea from './TextArea';
import Button from './Button';

interface SelectOption {
  value: string;
  label: string;
}

interface InvoiceHireItemsTableProps {
  register: UseFormRegister<InvoiceHire>;
  watch: UseFormWatch<InvoiceHire>;
  fields: FieldArrayWithId<InvoiceHire, 'items', 'id'>[];
  isDebitOrCredit: boolean;
  invoiceType: string;
  currencyOptions: SelectOption[];
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  getDefaultDescription: (type: string) => string;
  getDefaultUnit: (type: string) => string;
}

const InvoiceHireItemsTable: React.FC<InvoiceHireItemsTableProps> = ({
  register,
  watch,
  fields,
  isDebitOrCredit,
  invoiceType,
  currencyOptions,
  onAddItem,
  onRemoveItem,
  getDefaultDescription,
  getDefaultUnit,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Invoice Items</h2>
          <Button type="button" onClick={onAddItem} variant="secondary" size="sm">
            Add Item
          </Button>
        </div>
      </div>
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                {!isDebitOrCredit && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate USD</th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount USD</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Currency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-4 py-4">
                    <TextArea
                      {...register(`items.${index}.description`, { required: 'Description is required' })}
                      placeholder={getDefaultDescription(invoiceType)}
                      className="min-w-[200px]"
                      rows={2}
                    />
                  </td>
                  {!isDebitOrCredit && (
                    <>
                      <td className="px-4 py-4">
                        <Input
                          type="date"
                          {...register(`items.${index}.fromDate`, { required: 'From date is required' })}
                          className="min-w-[120px]"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <Input
                          type="date"
                          {...register(`items.${index}.toDate`, { required: 'To date is required' })}
                          className="min-w-[120px]"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <Input
                          {...register(`items.${index}.unit`, { required: 'Unit is required' })}
                          placeholder={getDefaultUnit(invoiceType)}
                          className="min-w-[80px]"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          {...register(`items.${index}.quantity`, { 
                            required: 'Quantity is required',
                            min: { value: 0, message: 'Quantity must be 0 or greater' }
                          })}
                          className="min-w-[80px]"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          {...register(`items.${index}.rateUSD`, { 
                            required: 'Rate is required',
                            min: { value: 0, message: 'Rate must be 0 or greater' }
                          })}
                          className="min-w-[100px]"
                        />
                      </td>
                    </>
                  )}
                  <td className="px-4 py-4">
                    <Input
                      type="number"
                      step="0.01"
                      {...register(`items.${index}.amountUSD`)}
                      className="min-w-[100px]"
                      disabled={!isDebitOrCredit}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <select
                      {...register(`items.${index}.currency`)}
                      className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-</option>
                      {currencyOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => onRemoveItem(index)}
                        variant="danger"
                        size="sm"
                      >
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={isDebitOrCredit ? 2 : 7} className="px-4 py-3 text-right font-semibold">Net Total:</td>
                <td className="px-4 py-3 font-semibold">${watch('netTotal')?.toFixed(2) || '0.00'}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceHireItemsTable;
